import {isFlexiCurrency, isPerksCurrency, isRewardCurrency, isValidFlexiCurrencyAmount} from "@shared/wallet"
import {PaymentCurrencyType, Wallet} from "@irati/types"

type SlimWallet = Prettify<Required<Pick<Wallet, "amount" | "currency">>>

interface CalculatePointsBlendBreakdown {
  RPWallet: SlimWallet
  TTWallet: SlimWallet
  currency: PaymentCurrencyType
  costInCents: number
  isPointsBlendEligible: boolean
}

export interface PointsBlendBreakdown {
  /**
   * Is this considered a blend payment, e.g even if RP = 0 and TT = 100, the
   * original price could have been RP, in which case we are making a TT
   * purchase with RP, therefore this would be true
   */
  isBlendPayment: boolean
  canAfford: boolean
  /** if user can't afford, how much by */
  amountShort?: number
  spent: {RP: SlimWallet; TT: SlimWallet}
  remaining: {RP: SlimWallet; TT: SlimWallet}
}

export function calculatePointsBreakdown({
  isPointsBlendEligible,
  costInCents,
  currency,
  RPWallet,
  TTWallet
}: CalculatePointsBlendBreakdown): PointsBlendBreakdown {
  if (!isFlexiCurrency(currency)) throw new Error(`Invalid currency, expected flexi currency got "${currency}"`)

  if (!isValidFlexiCurrencyAmount(costInCents)) throw new Error("Invalid fractional price, flexi prices should always be whole units")

  if (!isPerksCurrency(TTWallet.currency)) throw new Error(`Invalid wallet fetched, expected TT wallet, got "${TTWallet.currency}"`)

  if (!isRewardCurrency(RPWallet.currency)) throw new Error(`Invalid wallet fetched, expected RP wallet, got "${TTWallet.currency}"`)

  const {amount: rps = 0} = RPWallet
  const {amount: tts = 0} = TTWallet

  let usedTT: number
  let usedRP: number

  if (isPointsBlendEligible) {
    // always favour TTs first as these expire
    usedTT = Math.min(tts, costInCents)
    usedRP = Math.min(rps, costInCents - usedTT)
  } else {
    usedTT = isPerksCurrency(currency) ? Math.min(tts, costInCents) : 0
    usedRP = isRewardCurrency(currency) ? Math.min(rps, costInCents) : 0
  }

  const canAfford = usedTT + usedRP >= costInCents
  const remainingTT = tts - usedTT
  const remainingRP = rps - usedRP

  const isNotBlendPayment = (isRewardCurrency(currency) && usedTT === 0) || (isPerksCurrency(currency) && usedRP === 0)
  return {
    canAfford,
    ...(isPointsBlendEligible &&
      !canAfford && {
        amountShort: costInCents - rps - tts
      }),
    isBlendPayment: isPointsBlendEligible && canAfford && !isNotBlendPayment,
    spent: {RP: {currency: RPWallet.currency, amount: usedRP}, TT: {currency: TTWallet.currency, amount: usedTT}},
    remaining: {RP: {currency: RPWallet.currency, amount: remainingRP}, TT: {currency: TTWallet.currency, amount: remainingTT}}
  }
}
